import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ReactFlow,
  Background,
  MiniMap,
  applyNodeChanges,
} from "@xyflow/react";
import { Eye, EyeOff, Loader2, Network } from "lucide-react";
import PropTypes from "prop-types";
import DeviceNode from "../topology/DeviceNode";
import { applyDagreLayout } from "../../utils/layoutUtils";
import { getScenarioTopologyRelevance } from "../../api/services/labService";
import "../../assets/styles/colors.css";

// Kept in sync with TopologyPreviewModal: the same normalization + layout
// rules are reused so the inline tab renders identically to the box card's
// "Preview Topology" modal.
const VALID_EDGE_TYPES = new Set([
  "default",
  "straight",
  "step",
  "smoothstep",
  "bezier",
]);

const VIEW_MODES = ["icon", "compact", "full"];
const VIEW_MODE_LABELS = {
  icon: "Showing icons only — click for main fields",
  compact: "Showing main fields — click for all fields",
  full: "Showing all fields — click for icons only",
};

function normalizeNode(n, idx) {
  if (!n || n.id == null) return null;

  const nodeData = n.data && typeof n.data === "object" ? { ...n.data } : {};
  const nodeType = nodeData.nodeType || n.nodeType || n.type || "node";
  const label =
    nodeData.label || n.label || `${nodeType}: ${String(n.id ?? idx)}`;

  return {
    ...n,
    id: String(n.id ?? idx),
    type: "device",
    position: {
      x: (idx % 4) * 220,
      y: Math.floor(idx / 4) * 160,
    },
    data: {
      ...nodeData,
      nodeType,
      label,
    },
  };
}

function normalizeEdge(e, idx, edgeColor) {
  if (!e) return null;

  const source = String(e.source ?? e.from ?? "");
  const target = String(e.target ?? e.to ?? "");
  if (!source || !target) return null;

  const hasLabel = !!e.label;

  return {
    ...e,
    id: e.id || `e-${source}-${target}-${idx}`,
    source,
    target,
    type: VALID_EDGE_TYPES.has(e.type) ? e.type : "smoothstep",
    style: {
      ...(e.style || {}),
      stroke: edgeColor,
      strokeWidth: 2,
    },
    ...(hasLabel && {
      labelStyle: {
        fill: edgeColor,
        fontWeight: 900,
        fontSize: 14,
        cursor: "pointer",
      },
      labelBgStyle: {
        fill: edgeColor,
        fillOpacity: 1,
        stroke: edgeColor,
        strokeWidth: 2,
      },
      labelBgPadding: [5, 5],
      labelBgBorderRadius: 50,
      labelShowBg: true,
    }),
  };
}

// Amber stands apart from the green primary used by every other node/edge.
const HIGHLIGHT_COLOR = "var(--color-warning-light)";
const NO_WALKTHROUGH_NOTICE =
  "This box has no walkthrough, so no nodes are highlighted.";
const RELEVANCE_UNAVAILABLE_NOTICE = "Node relevance is unavailable right now.";

// Highlight is applied via the ReactFlow wrapper's style so the shared
// DeviceNode (also used by the editor canvas) stays untouched.
function highlightNode(node) {
  return {
    ...node,
    style: {
      ...node.style,
      borderRadius: 8,
      outline: `2px solid ${HIGHLIGHT_COLOR}`,
      boxShadow: `0 0 16px ${HIGHLIGHT_COLOR}`,
    },
  };
}

function getIsDarkMode() {
  if (typeof window === "undefined") return false;
  return (
    document.documentElement.classList.contains("dark") ||
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Sidebar icon button — matches TopologyPreviewModal styling. */
function SideBtn({ onClick, title, active, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200
        ${
          active
            ? "bg-(--color-primary)/20 text-(--color-primary)"
            : "text-(--color-primary) hover:bg-(--color-primary)/15 active:bg-(--color-primary)/25"
        }`}
    >
      {children}
    </button>
  );
}

SideBtn.propTypes = {
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string,
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

function ViewModeIcon({ viewMode }) {
  if (viewMode === "icon") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <line x1="8" y1="20" x2="16" y2="20" />
        <line x1="12" y1="16" x2="12" y2="20" />
      </svg>
    );
  }
  if (viewMode === "compact") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="4" y1="7" x2="20" y2="7" />
        <line x1="4" y1="12" x2="14" y2="12" />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="4" y1="14" x2="20" y2="14" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

ViewModeIcon.propTypes = { viewMode: PropTypes.string };

function ZoomInIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="11" y1="8" x2="11" y2="14" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function ZoomOutIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function FitViewIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    </svg>
  );
}

/**
 * Inline topology view for the Lab page tab. Renders the box's saved topology
 * (`topology_json`) using the same ReactFlow canvas as the box card's
 * "Preview Topology" modal, but embedded in the tab instead of a portal modal.
 */
function LabTopologyTab({
  scenarioId,
  nodes: inputNodes = [],
  edges: inputEdges = [],
}) {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [viewMode, setViewMode] = useState("compact");
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const reactFlowInstance = useRef(null);

  // Ids of nodes the walkthrough uses (core + path). Fetched on the first
  // toggle and reused afterwards; only an "applied" result is kept so
  // failures retry.
  const [activeIds, setActiveIds] = useState(null);
  const [isHighlightOn, setIsHighlightOn] = useState(false);
  const [isLoadingRelevance, setIsLoadingRelevance] = useState(false);
  const [relevanceNotice, setRelevanceNotice] = useState(null);

  const toggleHighlight = useCallback(async () => {
    if (isLoadingRelevance) return;
    setRelevanceNotice(null);
    if (activeIds) {
      setIsHighlightOn((on) => !on);
      return;
    }

    setIsLoadingRelevance(true);
    try {
      const res = await getScenarioTopologyRelevance(scenarioId);
      if (res?.relevance_status !== "applied") {
        setRelevanceNotice(
          res?.relevance_status === "no_walkthrough"
            ? NO_WALKTHROUGH_NOTICE
            : RELEVANCE_UNAVAILABLE_NOTICE,
        );
        return;
      }
      const relevanceNodes = res.topology_json?.nodes || [];
      setActiveIds(
        new Set(
          relevanceNodes.filter((n) => !n.data?.dimmed).map((n) => String(n.id)),
        ),
      );
      setIsHighlightOn(true);
    } catch {
      setRelevanceNotice(RELEVANCE_UNAVAILABLE_NOTICE);
    } finally {
      setIsLoadingRelevance(false);
    }
  }, [activeIds, isLoadingRelevance, scenarioId]);

  const displayNodes = useMemo(
    () =>
      isHighlightOn
        ? nodes.map((n) => (activeIds.has(n.id) ? highlightNode(n) : n))
        : nodes,
    [nodes, isHighlightOn, activeIds],
  );

  const hasTopology =
    Array.isArray(inputNodes) && inputNodes.length > 0;

  const cycleViewMode = useCallback(() => {
    setViewMode((prev) => {
      const idx = VIEW_MODES.indexOf(prev);
      return VIEW_MODES[(idx + 1) % VIEW_MODES.length];
    });
  }, []);

  const patchedNodeTypes = useMemo(
    () => ({
      device: (props) => <DeviceNode {...props} viewMode={viewMode} />,
    }),
    [viewMode],
  );

  // Normalize + auto-layout whenever the input data changes.
  useEffect(() => {
    const isDark = getIsDarkMode();
    const edgeColor = isDark ? "var(--color-primary)" : "#16a34a";

    let nodeList = Array.isArray(inputNodes)
      ? inputNodes.map(normalizeNode).filter(Boolean)
      : [];

    const edgeList = Array.isArray(inputEdges)
      ? inputEdges
          .map((e, idx) => normalizeEdge(e, idx, edgeColor))
          .filter(Boolean)
      : [];

    if (nodeList.length > 0) {
      try {
        nodeList = applyDagreLayout(nodeList, edgeList, { direction: "TB" });
      } catch (err) {
        console.warn(
          "LabTopologyTab: dagre layout failed, using grid fallback:",
          err,
        );
      }
    }

    setNodes(nodeList);
    setEdges(edgeList);
  }, [inputNodes, inputEdges]);

  const onNodesChange = useMemo(
    () => (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onInit = useMemo(
    () => (instance) => {
      reactFlowInstance.current = instance;
      setTimeout(() => instance.fitView({ padding: 0.4, duration: 300 }), 100);
    },
    [],
  );

  const handleZoomIn = useCallback(() => {
    reactFlowInstance.current?.zoomIn({ duration: 200 });
  }, []);

  const handleZoomOut = useCallback(() => {
    reactFlowInstance.current?.zoomOut({ duration: 200 });
  }, []);

  const handleFitView = useCallback(() => {
    reactFlowInstance.current?.fitView({ duration: 300, padding: 0.1 });
  }, []);

  if (!hasTopology) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 min-h-[45vh] text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-md border border-(--color-primary)/30 bg-transparent">
          <Network className="h-6 w-6 text-(--color-primary)" />
        </div>
        <h3 className="text-lg font-semibold text-(--color-text-primary)">
          {t("No topology available", {
            defaultValue: "No topology available",
          })}
        </h3>
        <p className="max-w-md text-sm text-(--color-text-muted)">
          {t("This box does not have a saved topology to display.", {
            defaultValue: "This box does not have a saved topology to display.",
          })}
        </p>
      </div>
    );
  }

  const isDarkMode = getIsDarkMode();

  return (
    <div className="relative h-[65vh] min-h-[45vh] rounded-md overflow-hidden border border-(--color-primary)/30 bg-transparent">
      {/* Left sidebar — view-mode + zoom controls */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-1.5 bg-(--color-bg-card) border border-(--color-primary)/25 rounded-md px-1 py-2">
        <SideBtn
          onClick={cycleViewMode}
          title={VIEW_MODE_LABELS[viewMode]}
          active={viewMode !== "icon"}
        >
          <ViewModeIcon viewMode={viewMode} />
        </SideBtn>

        <div className="w-5 h-px bg-(--color-primary)/20 my-0.5" />

        <SideBtn onClick={handleZoomIn} title="Zoom in">
          <ZoomInIcon />
        </SideBtn>

        <SideBtn onClick={handleZoomOut} title="Zoom out">
          <ZoomOutIcon />
        </SideBtn>

        <SideBtn onClick={handleFitView} title="Fit to view">
          <FitViewIcon />
        </SideBtn>

        <div className="w-5 h-px bg-(--color-primary)/20 my-0.5" />

        <SideBtn
          onClick={toggleHighlight}
          title={
            isHighlightOn
              ? "Hide highlight"
              : "Highlight nodes used in this lab"
          }
          active={isHighlightOn}
        >
          {isLoadingRelevance ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isHighlightOn ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </SideBtn>
      </div>

      {relevanceNotice && (
        <div className="absolute left-1/2 top-3 -translate-x-1/2 z-20 rounded-md border border-(--color-primary)/25 bg-(--color-bg-card) px-3 py-1.5 text-xs text-(--color-text-muted)">
          {relevanceNotice}
        </div>
      )}

      <ReactFlow
        nodes={displayNodes}
        edges={edges}
        nodeTypes={patchedNodeTypes}
        onNodesChange={onNodesChange}
        onInit={onInit}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        fitView
        minZoom={0.05}
        onEdgeMouseEnter={(event, edge) => {
          if (edge?.data && Object.keys(edge.data).length > 0) {
            setHoveredEdge(edge);
            setTooltipPos({ x: event.clientX, y: event.clientY });
          }
        }}
        onEdgeMouseMove={(event) => {
          setTooltipPos({ x: event.clientX, y: event.clientY });
        }}
        onEdgeMouseLeave={() => setHoveredEdge(null)}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: "smoothstep",
          style: {
            stroke: isDarkMode ? "var(--color-primary)" : "#16a34a",
            strokeWidth: 2,
          },
        }}
      >
        <Background gap={16} size={1} color="var(--color-primary-50)" />
        <MiniMap
          pannable
          zoomable
          style={{
            width: 120,
            height: 80,
            backgroundColor: isDarkMode
              ? "rgb(15 23 42 / 0.95)"
              : "rgb(255 255 255 / 0.95)",
            border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
            borderRadius: "8px",
            boxShadow: isDarkMode
              ? "0 4px 6px rgb(0 0 0 / 0.3)"
              : "0 1px 3px rgb(0 0 0 / 0.1)",
          }}
          nodeColor={() => "var(--color-primary)"}
          maskColor={
            isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.1)"
          }
        />
      </ReactFlow>

      {/* Edge hover tooltip */}
      {hoveredEdge && (
        <div
          className="fixed z-1100 pointer-events-none rounded-sm border border-(--color-border) bg-(--color-bg-card) shadow-[0_0_20px_var(--color-shadow-primary)] px-3 py-2 text-xs min-w-45 max-w-xs"
          style={{ left: tooltipPos.x + 12, top: tooltipPos.y + 12 }}
        >
          <div className="text-[10px] font-semibold text-(--color-text-muted) uppercase tracking-wide mb-1.5">
            {hoveredEdge.source} → {hoveredEdge.target}
          </div>
          <div className="space-y-1">
            {Object.entries(hoveredEdge.data || {}).map(([key, value]) => (
              <div key={key} className="flex gap-2 justify-between">
                <span className="text-(--color-text-muted)">{key}</span>
                <span className="font-mono text-(--color-text-white) truncate">
                  {typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

LabTopologyTab.propTypes = {
  scenarioId: PropTypes.string,
  nodes: PropTypes.arrayOf(PropTypes.object),
  edges: PropTypes.arrayOf(PropTypes.object),
};

export default LabTopologyTab;
